import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Appointment.css";
import { FaCalendarAlt, FaEnvelope, FaUser } from "react-icons/fa";

const sectionsData = [
  { title: "Personal", icon: <FaUser /> },
  { title: "Contact", icon: <FaEnvelope /> },
  { title: "Appointment", icon: <FaCalendarAlt /> },
];
const steps = [
  { id: 1, field: "name", label: "Full Name", type: "text" },
  { id: 2, field: "city", label: "City", type: "text" },
  { id: 3, field: "email", label: "Email", type: "email" },
  { id: 4, field: "phone", label: "Phone Number", type: "tel" },
  { id: 5, field: "date", label: "Date", type: "date" },
  { id: 6, field: "time", label: "Time", type: "time" },
  { id: 7, field: "symptoms", label: "Symptoms", type: "checkboxes" },
  { id: 8, field: "message", label: "Message", type: "textarea" },
];

const symptomsList = [
  "Irregular Periods",
  "Weight Gain",
  "Hair Loss",
  "Acne",
  "Fatigue",
  "Other",
];

const Appointment = () => {
  const [missing, setMissing] = useState(false);
  const [step, setStep] = useState(1);
  const inputRefs = useRef({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    date: "",
    time: "",
    message: "",
    symptoms: [],
  });
  const generateId = () =>
    Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
  const [formId, setFormId] = useState(generateId);

  const total = steps.length;
  const progress = ((step - 1) / (total - 1)) * 100;
  const current = steps.find((s) => s.id === step);

  const validateField = (field, value) => {
    if (!value) return false;
    if (field === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (field === "phone") return /^[0-9]{7,15}$/.test(value);
    return true;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (step < total) {
        nextStep();
      } else {
        handleSubmit();
      }
    }
  };

  const nextStep = async () => {
    if (current.field === "symptoms") {
      if (formData.symptoms.length === 0) {
        setMissing(true);
        return;
      }
    } else {
      if (!validateField(current.field, formData[current.field])) {
        setMissing(true);
        return;
      }
    }
    savePartial();
    setStep((prev) => Math.min(prev + 1, total));
  };
  const prevStep = () => {
    setMissing(false);
    setStep((prev) => Math.max(prev - 1, 1));
  };
  const savePartial = async (submit = false) => {
    try {
      const now = new Date();
      const sentAt = `${now.getDate()}/${
        now.getMonth() + 1
      }/${now.getFullYear()} ${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;

      let formattedDate = formData.date;
      if (formData.date && formData.date.includes("-")) {
        const [year, month, day] = formData.date.split("-");
        formattedDate = `${day}/${month}/${year}`;
      }

      const params = new URLSearchParams({
        formId,
        ...formData,
        date: formattedDate,
        symptoms: formData.symptoms.join(","),
        source: "social media",
        sentAt,
        submit,
      });

      const res = await fetch(
        `https://script.google.com/macros/s/AKfycbxHH8ZjufWrL8hWRrHRWC1bpPR4SqaoAVEAmEMCSNLYimngqxPkz0eCYRJzKA10pUqXXA/exec?${params.toString()}`,
        { method: "GET" }
      );

      let data;
      try {
        data = JSON.parse(await res.text());
      } catch (err) {
        throw new Error("Not JSON!");
      }
      return data;
    } catch (err) {
      return { status: "error", message: err.message };
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const result = await savePartial(true);
      if (result.status === "success") {
        alert("Appointment booked successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          city: "",
          date: "",
          time: "",
          message: "",
          symptoms: [],
        });
        setStep(1);
        setFormId(generateId());
      } else {
        alert("Error: " + result.message);
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-screen pt-20 flex items-center justify-center bg-pink-50">
      <div className="w-full h-full sm:h-auto sm:max-w-3xl bg-white  rounded-xl shadow-xl shadow-gray-400 p-2 sm:p-8 flex flex-col gap-8">
        <div className="flex w-full flex-col sm:flex-row  justify-center items-center gap-2 sm:gap-12">
          {sectionsData.map((section, index) => {
            let filled = false;
            if (index === 0 && progress <= 25) filled = true;
            if (index === 1 && progress > 25 && progress <= 55) filled = true;
            if (index === 2 && progress > 55) filled = true;

            return (
              <motion.div
                key={section.title}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className={`flex gap-2 sm:flex-col items-center justify-center w-full p-2 sm:p-0 sm:w-24 sm:h-24 rounded-xl shadow-md overflow-hidden ${
                  filled
                    ? "bg-pink-600 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                <div className="text-2xl mb-2">{section.icon}</div>
                <div className="text-sm font-medium">{section.title}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
          <motion.div
            className="bg-pink-600 h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.3 }}
            onAnimationComplete={() => {
              inputRefs.current[step]?.focus();
            }}
          >
            <div className="relative z-0 w-full">
              {current.type === "checkboxes" ? (
                <div className="flex flex-col gap-3  pt-3">
                  {symptomsList.map((symptom, i) => {
                    const selected = formData.symptoms.includes(symptom);
                    return (
                      <label
                        key={symptom}
                        className={`flex items-center gap-3 px-3 cursor-pointer transition w-fit`}
                      >
                        <input
                          ref={(el) =>
                            i === 0
                              ? (inputRefs.current[current.id] = el)
                              : null
                          }
                          type="checkbox"
                          value={symptom}
                          checked={selected}
                          onKeyDown={handleKeyDown}
                          onChange={(e) => {
                            setMissing(false);
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                symptoms: [...formData.symptoms, symptom],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                symptoms: formData.symptoms.filter(
                                  (s) => s !== symptom
                                ),
                              });
                            }
                          }}
                          className="h-4 w-4 accent-pink-600 cursor-pointer"
                        />
                        <span
                          className={`text-sm font-medium transition ${
                            selected ? "text-pink-700" : "text-gray-700"
                          }`}
                        >
                          {symptom}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : current.type !== "textarea" ? (
                <input
                  ref={(el) => (inputRefs.current[current.id] = el)}
                  type={current.type}
                  name={current.field}
                  placeholder=" "
                  value={formData[current.field]}
                  onChange={(e) => {
                    setMissing(false);
                    setFormData({
                      ...formData,
                      [current.field]: e.target.value,
                    });
                  }}
                  onKeyDown={handleKeyDown}
                  className="peer block w-full border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2 text-gray-900 focus:border-pink-600 focus:outline-none focus:ring-0 sm:text-sm"
                  required
                />
              ) : (
                <textarea
                  ref={(el) => (inputRefs.current[current.id] = el)}
                  name={current.field}
                  placeholder=" "
                  value={formData[current.field]}
                  onChange={(e) => {
                    setMissing(false);
                    setFormData({
                      ...formData,
                      [current.field]: e.target.value,
                    });
                  }}
                  onKeyDown={handleKeyDown}
                  className="peer block w-full resize-none appearance-none border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2 text-gray-900 focus:border-pink-600 focus:outline-none focus:ring-0 sm:text-sm"
                  rows={4}
                  required
                />
              )}
              <label className="absolute left-0 -top-3.5 text-gray-500 text-sm transition-all peer-placeholder-shown:top-2.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base peer-focus:-top-3.5 peer-focus:text-gray-500 peer-focus:text-sm">
                {current.label}
              </label>
            </div>
            {missing && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.3 }}
                className="text-red-500 text-sm mt-1"
              >
                Please enter a valid {current.label}
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        <div
          className={`flex flex-col gap-4 sm:flex-row ${
            step === 1 ? "justify-end" : "justify-between"
          } mt-6`}
        >
          {step > 1 && (
            <button
              disabled={loading}
              onClick={prevStep}
              className="group relative inline-flex h-12 items-center justify-center overflow-hidden rounded-md bg-neutral-950 px-6 font-medium text-neutral-200 duration-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="translate-x-0 opacity-100 transition group-hover:-translate-x-[150%] group-hover:opacity-0">
                Previous
              </div>
              <div className="absolute translate-x-[150%] opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100 ">
                Previous
              </div>
            </button>
          )}

          {step < total ? (
            <button
              disabled={loading}
              onClick={nextStep}
              className="group relative inline-flex h-12 items-center justify-center overflow-hidden rounded-md bg-neutral-950 px-6 font-medium text-neutral-200 duration-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="translate-x-0 opacity-100 transition group-hover:-translate-x-[150%] group-hover:opacity-0">
                Next
              </div>
              <div className="absolute translate-x-[150%] opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100">
                Next
              </div>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="ml-auto px-6 py-3 bg-green-800 text-white rounded-md hover:bg-green-600 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Appointment;
